import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { formatDate } from '@/lib/dates';
import { Badge, Button, Input, Money, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from '@/features/common/ui';

/**
 * Refunds on screen (R-04): money back on a cost, never income.
 *
 * The API stores a refund as a NEGATIVE row in the refunded charge's own
 * category, so every total nets by itself, and the money arriving as a bank
 * DEPOSIT that no income figure counts. These two sheets are the two ways a
 * person gets there:
 *
 *   - from the CHARGE - "this came back" - where the deposit is written for
 *     them (`ExpenseRefundsSheet`);
 *   - from the CREDIT already in the bank - "this money is that charge coming
 *     back" - where the statement line is adopted rather than written twice
 *     (`RefundOfCostPicker`, inside the Banking deposit sheet).
 *
 * Both call the same route. The second is the one a statement import needs:
 * the ₹1,169 Hostinger reversal and the ₹3 Facebook refund were both sitting
 * in the bank as deposits filed under Other income.
 */

interface ExpenseDetail {
  id: string;
  date: string;
  totalAmount: string;
  currency: string;
  notes: string | null;
  status: 'recorded' | 'draft';
  isRefund: boolean;
  refunds: Array<{ id: string; date: string; amount: string; referenceNumber: string | null; notes: string | null }>;
  refundedAmount: string;
  netAmountAfterRefunds: string;
}

/** Today in the browser's terms - a default a person can change, never a figure. */
const today = () => new Date().toISOString().slice(0, 10);

/**
 * A charge, the money that came back against it, and what it cost once it
 * did. "Record a refund" writes the deposit too; deleting a refund here takes
 * it back off (an adopted statement line goes back to being an unexplained
 * credit, which the review will ask about).
 */
export function ExpenseRefundsSheet({
  expenseId,
  categoryLabel,
  canRecord,
  onClose,
  onChanged,
}: {
  expenseId: string;
  categoryLabel: string;
  canRecord: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const detail = useQuery({
    queryKey: ['expense', org?.id, expenseId],
    queryFn: () => api.get<ExpenseDetail>(`/expenses/${expenseId}`),
    enabled: !!org?.id,
  });
  const charge = detail.data;
  const [form, setForm] = useState({ date: today(), amount: '', referenceNumber: '' });
  // The amount offered is what is LEFT to refund - the usual case is the whole
  // charge coming back, and a person reversing a duplicate should not retype it.
  const amount = form.amount || charge?.netAmountAfterRefunds || '';

  const record = useMutation({
    mutationFn: () =>
      api.post<ExpenseDetail>(`/expenses/${expenseId}/refunds`, {
        date: form.date,
        amount,
        ...(form.referenceNumber.trim() ? { referenceNumber: form.referenceNumber.trim() } : {}),
      }),
    onSuccess: () => {
      setForm({ date: today(), amount: '', referenceNumber: '' });
      void detail.refetch();
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (refundId: string) => api.delete(`/expenses/${refundId}`),
    onSuccess: () => {
      void detail.refetch();
      onChanged();
    },
  });
  const error = record.error instanceof ApiError ? record.error : remove.error instanceof ApiError ? remove.error : undefined;
  const fullyRefunded = !!charge && Number(charge.netAmountAfterRefunds) <= 0;

  return (
    <Sheet label="Refunds" onClose={onClose}>
      <SheetHeader title="Refunds" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          if (amount && form.date) record.mutate();
        }}
      >
        <SheetBody>
          {charge ? (
            <>
              <div className="rounded-md bg-muted px-3 py-2 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate">{categoryLabel}</span>
                  <span className="tabular shrink-0 font-medium">
                    <Money value={charge.totalAmount} currency={charge.currency} />
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDate(charge.date)}
                  {charge.notes ? ` · ${charge.notes}` : ''}
                </div>
              </div>

              {charge.refunds.length ? (
                <ul className="divide-y divide-border rounded-md border border-border text-sm" data-testid="expense-refunds">
                  {charge.refunds.map((refund) => (
                    <li key={refund.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid="expense-refund">
                      <span className="min-w-0">
                        <span className="text-muted-foreground">{formatDate(refund.date)}</span>
                        {refund.referenceNumber ? <span className="text-muted-foreground"> · {refund.referenceNumber}</span> : null}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span className="tabular text-success">
                          <Money value={refund.amount} currency={charge.currency} /> back
                        </span>
                        {canRecord ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            loading={remove.isPending && remove.variables === refund.id}
                            onClick={() => remove.mutate(refund.id)}
                            aria-label={`Delete the refund of ${formatDate(refund.date)}`}
                          >
                            Delete
                          </Button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Nothing has come back against this charge.</p>
              )}

              {/* What it actually cost - the figure every report now uses. */}
              <div className="flex items-baseline justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid="expense-net">
                <span>Cost after refunds</span>
                <span className="tabular font-medium">
                  <Money value={charge.netAmountAfterRefunds} currency={charge.currency} />
                </span>
              </div>

              {canRecord && !fullyRefunded ? (
                <div className="space-y-3 rounded-md border border-dashed border-border p-3">
                  <p className="text-xs text-muted-foreground">
                    Money back on this charge - a vendor refund, a reversed duplicate. It comes off this category&rsquo;s
                    spend and goes into the account, and it is never counted as income. If the credit is already on a
                    statement you imported, file it from that line in Banking instead, so it is not entered twice.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input
                      label="Came back on"
                      type="date"
                      required
                      value={form.date}
                      onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                      error={error?.fieldError('date')}
                      data-testid="refund-date"
                    />
                    <Input
                      label="Amount back"
                      inputMode="decimal"
                      required
                      value={amount}
                      onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                      error={error?.fieldError('amount')}
                      data-testid="refund-amount"
                    />
                  </div>
                  <Input
                    label="Reference (optional)"
                    value={form.referenceNumber}
                    onChange={(e) => setForm((f) => ({ ...f, referenceNumber: e.target.value }))}
                    placeholder="CRV POS RAZ*HOSTINGER"
                  />
                </div>
              ) : null}

              {error && !error.errors?.length ? (
                <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error.message}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </SheetBody>
        <SheetFooter>
          {canRecord && charge && !fullyRefunded ? (
            <Button type="submit" loading={record.isPending} data-testid="refund-record">
              Record refund
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

interface CandidateRow {
  id: string;
  date: string;
  categoryName: string;
  totalAmount: string;
  currency: string;
  notes: string | null;
  referenceNumber: string | null;
  accountId?: string;
  isRefund?: boolean;
  isDraft?: boolean;
}

/** A credit in the bank, as far as the picker needs it. */
export interface CreditLine {
  id: string;
  accountId: string;
  date: string;
  amount: string;
  currency: string;
  description: string | null;
  payee: string | null;
}

/**
 * The words a bank line and a charge might share: "RAZ*HOSTINGER" and
 * "Hostinger (Razorpay autopay)" share "hostinger". The rail and the
 * boilerplate a bank wraps around a name are dropped, as the Inbox's payee
 * key does - "CRV POS" is on every card refund and says nothing about whose.
 */
const NOISE = new Set(['pos', 'crv', 'upi', 'neft', 'imps', 'rtgs', 'ach', 'refund', 'reversal', 'credit', 'debit', 'from', 'the', 'and', 'for', 'ltd', 'pvt', 'india']);
function wordsOf(text: string | null | undefined): Set<string> {
  return new Set(
    (text ?? '')
      .toLowerCase()
      .split(/[^a-z]+/)
      .filter((w) => w.length >= 3 && !NOISE.has(w)),
  );
}

/** Days from `a` back to `b` (both YYYY-MM-DD). */
const daysBetween = (a: string, b: string) => Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000;

/**
 * Which charge a credit already in the bank is the refund of.
 *
 * Recent recorded charges on the SAME account, dated on or before the credit
 * and at least as large as it - the only ones the server would accept - best
 * first: the exact same amount (a reversed duplicate is always that), then
 * shared words with the bank line, then the nearest date. The person picks;
 * the credit is adopted as the refund's cash side, so the balance does not
 * move twice.
 */
export function RefundOfCostPicker({
  credit,
  onDone,
  onCancel,
}: {
  credit: CreditLine;
  onDone: () => void;
  onCancel: () => void;
}) {
  const org = useActiveOrg();
  const from = new Date(Date.parse(`${credit.date}T00:00:00Z`) - 180 * 86_400_000).toISOString().slice(0, 10);
  const candidates = useQuery({
    queryKey: ['refund-candidates', org?.id, credit.id],
    queryFn: () =>
      api.get<{ items: CandidateRow[] }>(
        `/expenses${qs({ accountId: credit.accountId, from, to: credit.date, status: 'recorded', limit: 200 })}`,
      ),
    enabled: !!org?.id,
  });
  const [picked, setPicked] = useState<string | null>(null);

  const ranked = useMemo(() => {
    const words = wordsOf(`${credit.description ?? ''} ${credit.payee ?? ''}`);
    const amount = Number(credit.amount);
    return (candidates.data?.items ?? [])
      .filter((c) => !c.isRefund && !c.isDraft && Number(c.totalAmount) >= amount)
      .map((c) => {
        const theirs = wordsOf(`${c.notes ?? ''} ${c.referenceNumber ?? ''} ${c.categoryName}`);
        const shared = [...words].filter((w) => theirs.has(w)).length;
        return { c, same: Number(c.totalAmount) === amount, shared, days: daysBetween(credit.date, c.date) };
      })
      .sort((a, b) => Number(b.same) - Number(a.same) || b.shared - a.shared || a.days - b.days)
      .slice(0, 20);
  }, [candidates.data, credit]);

  const file = useMutation({
    mutationFn: (expenseId: string) =>
      api.post(`/expenses/${expenseId}/refunds`, {
        date: credit.date,
        amount: credit.amount,
        bankTransactionId: credit.id,
      }),
    onSuccess: onDone,
  });
  const error = file.error instanceof ApiError ? file.error : undefined;

  return (
    <div className="space-y-3" data-testid="refund-picker">
      <p className="text-sm">Which charge is this money coming back against?</p>
      {candidates.isLoading ? (
        <p className="text-sm text-muted-foreground">Looking…</p>
      ) : ranked.length ? (
        <ul className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border text-sm">
          {ranked.map(({ c, same }) => (
            <li key={c.id}>
              <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent">
                <input
                  type="radio"
                  name="refund-of"
                  className="size-4"
                  checked={picked === c.id}
                  onChange={() => setPicked(c.id)}
                  data-testid="refund-candidate"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    {c.categoryName}
                    {c.notes ? ` · ${c.notes}` : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">{formatDate(c.date)}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {same ? <Badge variant="success">Same amount</Badge> : null}
                  <Money value={c.totalAmount} currency={c.currency} />
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No charge on this account in the six months before it is large enough to be what this refunds.
        </p>
      )}
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error.message}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!picked}
          loading={file.isPending}
          onClick={() => picked && file.mutate(picked)}
          data-testid="refund-file"
        >
          File as a refund of this charge
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Back
        </Button>
      </div>
    </div>
  );
}
